import axios from "axios";
import { toast } from "react-toastify";

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api`,
  withCredentials: true, // 🟢 Cho phép gửi cookie refresh token
});

// Gắn access token vào request
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// Trạng thái ghi nhận đang refresh để tránh gọi nhiều lần
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Xử lý response
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // 🔥 Nếu lỗi 401 và chưa thử refresh token → thử refresh
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Đợi refresh token hoàn tất
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers["Authorization"] = "Bearer " + newToken;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        // Gọi API refresh access token
        const refreshRes = await axios.post(
          `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const newToken = refreshRes.data.token;

        // Lưu access token mới + modules (BE rebuild quyền từ DB mỗi lần refresh)
        localStorage.setItem("token", newToken);
        if (Array.isArray(refreshRes.data.modules)) {
          localStorage.setItem("modules", JSON.stringify(refreshRes.data.modules));
        }

        // Gắn vào header mặc định
        axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

        processQueue(null, newToken);
        isRefreshing = false;

        // Retry request cũ
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);

      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        // Refresh token cũng hết hạn → logout
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("modules");

        toast.error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
        setTimeout(() => (window.location.href = "/login"), 1000);

        return Promise.reject(refreshError);
      }
    }

    // ❌ Bảo trì / tắt app tạm thời
    if (status === 503 && error.response?.data?.code === "APP_MAINTENANCE") {
      window.dispatchEvent(
        new CustomEvent("app-maintenance-blocked", {
          detail: {
            enabled: true,
            title: error.response.data.title || error.response.data.message,
            message: error.response.data.maintenance_message,
            enabled_at: error.response.data.enabled_at,
          },
        })
      );
      return Promise.reject(error);
    }

    // ❌ Quyền không đủ (bỏ qua toast nếu caller tự xử lý / auth login)
    const errCode = error.response?.data?.code;
    const isAuthLogin =
      typeof originalRequest?.url === "string" &&
      originalRequest.url.includes("/auth/login");
    const skipAuthDeviceToast =
      isAuthLogin ||
      errCode === "TEACHER_DEVICE_LIMIT" ||
      errCode === "APP_AUDIENCE_DENIED" ||
      errCode === "DEVICE_FINGERPRINT_REQUIRED";

    if (
      status === 403 &&
      !originalRequest.skipGlobalErrorToast &&
      !skipAuthDeviceToast
    ) {
      toast.error(error.response?.data?.message || "Bạn không có quyền thực hiện chức năng này");
    }

    // ❌ Lỗi server
    if (status >= 500 && !originalRequest.skipGlobalErrorToast) {
      toast.error("Lỗi hệ thống, vui lòng thử lại sau");
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
